import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
	Store,
	Package,
	Search,
	RefreshCw,
	Play,
	Clock,
	CircleCheck as CheckCircle2,
	ArrowUpDown,
	ChevronDown,
	Filter,
	Loader as Loader2,
	TriangleAlert as AlertTriangle,
	ShoppingBag,
} from 'lucide-react'
import { Button } from '../components/ui/Button'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { getRelativeTime } from '../types/shopify'
import type { SortOption } from '../types/worker'
import { useShopReceiptQueue, type ShopReceiptQueueItem } from '../hooks/useShopReceiptQueue'
import { ShopReceiptModal } from '../components/shop-terminal/ShopReceiptModal'
import { getAdminId } from '../utils/getAdminId'

interface ShopTerminalStats {
	toConfirm: number
	confirmedToday: number
	discrepanciesToday: number
}

export const ShopTerminal: React.FC = () => {
	const { user } = useAuth()
	const [stats, setStats] = useState<ShopTerminalStats>({
		toConfirm: 0,
		confirmedToday: 0,
		discrepanciesToday: 0,
	})
	const [statsLoading, setStatsLoading] = useState(true)
	const [searchQuery, setSearchQuery] = useState('')
	const [sortBy, setSortBy] = useState<SortOption>('oldest')
	const [showSortMenu, setShowSortMenu] = useState(false)
	const [lastUpdated, setLastUpdated] = useState<Date>(new Date())
	const [refreshing, setRefreshing] = useState(false)
	const [selected, setSelected] = useState<ShopReceiptQueueItem | null>(null)

	const adminId = useMemo(() => (user ? getAdminId(user) : null), [user])

	const {
		deliveries,
		loading,
		error: queueError,
		totalCount,
		hasMore,
		loadDeliveries,
		loadMore,
		refresh,
		clearError,
	} = useShopReceiptQueue({ adminId, pageSize: 25, sortBy })

	const loadStats = useCallback(async () => {
		if (!adminId) return
		setStatsLoading(true)
		try {
			const startOfDay = new Date()
			startOfDay.setHours(0, 0, 0, 0)

			const { data, error } = await supabase.rpc('get_shop_receipt_submissions', {
				p_admin_id: adminId,
				p_from: startOfDay.toISOString(),
				p_to: null,
				p_limit: 500,
			})
			if (error) throw error

			const rows = (data ?? []) as { missing_units: number; extra_units: number }[]
			setStats((prev) => ({
				toConfirm: prev.toConfirm,
				confirmedToday: rows.length,
				discrepanciesToday: rows.filter((r) => (r.missing_units ?? 0) > 0 || (r.extra_units ?? 0) > 0).length,
			}))
		} catch (err) {
			console.error('Failed to load shop terminal stats:', err)
		} finally {
			setStatsLoading(false)
		}
	}, [adminId])

	useEffect(() => {
		loadStats()
		loadDeliveries()
	}, [loadStats, loadDeliveries])

	useEffect(() => {
		setStats((prev) => ({ ...prev, toConfirm: totalCount }))
	}, [totalCount])

	useEffect(() => {
		if (!loading) setLastUpdated(new Date())
	}, [loading, deliveries])

	const handleRefresh = async () => {
		setRefreshing(true)
		clearError()
		await Promise.all([loadStats(), refresh()])
		setRefreshing(false)
	}

	const filtered = useMemo(() => {
		if (!searchQuery.trim()) return deliveries
		const q = searchQuery.toLowerCase()
		return deliveries.filter(
			(d) =>
				d.reservation_number.toLowerCase().includes(q) ||
				(d.customer_name && d.customer_name.toLowerCase().includes(q)) ||
				(d.source_order_number && d.source_order_number.toLowerCase().includes(q)) ||
				d.items.some(
					(item) =>
						(item.sku && item.sku.toLowerCase().includes(q)) ||
						(item.name && item.name.toLowerCase().includes(q))
				)
		)
	}, [deliveries, searchQuery])

	const handleComplete = async () => {
		setSelected(null)
		await Promise.all([loadStats(), refresh()])
	}

	const displayedCount = filtered.length

	return (
		<div className="min-h-screen bg-gray-50 dark:bg-gray-900">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
				<div className="mb-8">
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
						<div className="flex items-center gap-4">
							<div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
								<Store className="h-7 w-7 text-white" />
							</div>
							<div>
								<h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Shop Terminal</h1>
								<p className="text-sm text-gray-500 dark:text-gray-400">
									Confirm what actually arrived from the warehouse
								</p>
							</div>
						</div>
						<div className="flex items-center gap-3">
							<span className="text-xs text-gray-500 dark:text-gray-400">
								Updated {getRelativeTime(lastUpdated.toISOString())}
							</span>
							<Button variant="secondary" size="sm" onClick={handleRefresh} disabled={refreshing}>
								<RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
								Refresh
							</Button>
						</div>
					</div>
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
					<div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
						<div className="flex items-center justify-between mb-4">
							<div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
								<Package className="h-6 w-6 text-amber-600 dark:text-amber-400" />
							</div>
							{loading ? <Loader2 className="h-5 w-5 animate-spin text-gray-400" /> : null}
						</div>
						<p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Deliveries to Confirm</p>
						<p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{stats.toConfirm}</p>
					</div>

					<div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
						<div className="flex items-center justify-between mb-4">
							<div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
								<AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
							</div>
							{statsLoading ? <Loader2 className="h-5 w-5 animate-spin text-gray-400" /> : null}
						</div>
						<p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
							With Discrepancies Today
						</p>
						<p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
							{statsLoading ? '-' : stats.discrepanciesToday}
						</p>
					</div>

					<div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 shadow-lg">
						<div className="flex items-center justify-between mb-4">
							<div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
								<CheckCircle2 className="h-6 w-6 text-white" />
							</div>
						</div>
						<p className="text-sm font-medium text-white/80 mb-1">Confirmed Today</p>
						<p className="text-3xl font-bold text-white">{statsLoading ? '-' : stats.confirmedToday}</p>
					</div>
				</div>

				{queueError && (
					<div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex items-center justify-between">
						<div className="flex items-center gap-3">
							<AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
							<p className="text-sm text-amber-700 dark:text-amber-300">{queueError.message}</p>
						</div>
						{queueError.retryable && (
							<Button variant="secondary" size="sm" onClick={handleRefresh} disabled={refreshing}>
								{refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Retry'}
							</Button>
						)}
					</div>
				)}

				<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
					<div className="p-4 sm:p-6 border-b border-gray-100 dark:border-gray-700">
						<div className="flex flex-col sm:flex-row gap-4">
							<div className="flex-1 relative">
								<Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
								<input
									type="text"
									placeholder="Search by reservation #, customer, SKU..."
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
								/>
							</div>
							<div className="flex gap-3">
								<div className="relative">
									<button
										onClick={() => setShowSortMenu(!showSortMenu)}
										className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
									>
										<ArrowUpDown className="h-4 w-4" />
										<span className="hidden sm:inline">
											{sortBy === 'oldest' ? 'Oldest First' : 'Newest First'}
										</span>
										<ChevronDown className="h-4 w-4" />
									</button>
									{showSortMenu && (
										<>
											<div className="fixed inset-0 z-10" onClick={() => setShowSortMenu(false)} />
											<div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-20 overflow-hidden">
												{(['oldest', 'newest'] as SortOption[]).map((option) => (
													<button
														key={option}
														onClick={() => {
															setSortBy(option)
															setShowSortMenu(false)
														}}
														className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
															sortBy === option
																? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
																: 'text-gray-700 dark:text-gray-300'
														}`}
													>
														{option === 'oldest' ? 'Oldest First (Recommended)' : 'Newest First'}
													</button>
												))}
											</div>
										</>
									)}
								</div>
								<div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600">
									<Filter className="h-4 w-4 text-gray-400" />
									<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
										{searchQuery ? `${displayedCount} found` : `${displayedCount} of ${totalCount}`}
									</span>
								</div>
							</div>
						</div>
					</div>

					<div className="divide-y divide-gray-100 dark:divide-gray-700">
						{loading && deliveries.length === 0 ? (
							<div className="p-12 text-center">
								<Loader2 className="h-10 w-10 animate-spin text-emerald-500 mx-auto mb-4" />
								<p className="text-gray-500 dark:text-gray-400">Loading deliveries...</p>
							</div>
						) : filtered.length === 0 ? (
							<div className="p-12 text-center">
								<div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
									<Package className="h-8 w-8 text-gray-400" />
								</div>
								<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
									{searchQuery ? 'No matching deliveries' : 'All caught up!'}
								</h3>
								<p className="text-gray-500 dark:text-gray-400">
									{searchQuery
										? 'Try adjusting your search terms'
										: 'Every delivery from the warehouse has been confirmed.'}
								</p>
							</div>
						) : (
							filtered.map((delivery) => (
								<div
									key={delivery.reservation_id}
									className="p-4 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
								>
									<div className="flex flex-col lg:flex-row lg:items-center gap-4">
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-3 mb-2 flex-wrap">
												<h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
													{delivery.reservation_number}
												</h3>
												{delivery.receipt_id && delivery.receipt_status === 'open' && (
													<span className="px-2.5 py-1 text-xs font-medium rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
														Started
													</span>
												)}
												{delivery.is_store_pickup && (
													<span className="px-2.5 py-1 text-xs font-medium rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 flex items-center gap-1">
														<ShoppingBag className="h-3 w-3" /> Shopify
													</span>
												)}
											</div>

											<div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
												{delivery.fulfilled_at && (
													<span className="flex items-center gap-1.5">
														<Clock className="h-4 w-4" />
														Sent {getRelativeTime(delivery.fulfilled_at)}
													</span>
												)}
												<span>{delivery.customer_name || 'Customer'}</span>
												{delivery.worker_name && <span>Picked by {delivery.worker_name}</span>}
												{delivery.source_order_number && <span>Order {delivery.source_order_number}</span>}
												<span className="flex items-center gap-1.5">
													<Package className="h-4 w-4" />
													{delivery.unit_count} units across {delivery.item_count} items
												</span>
											</div>

											{delivery.items.length > 0 && (
												<div className="mt-3 flex flex-wrap gap-2">
													{delivery.items.slice(0, 3).map((item) => (
														<span
															key={item.item_id}
															className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-lg"
														>
															{item.sku || (item.name || '').substring(0, 20)}
															{item.expected_qty > 1 && ` x${item.expected_qty}`}
														</span>
													))}
													{delivery.items.length > 3 && (
														<span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs rounded-lg">
															+{delivery.items.length - 3} more
														</span>
													)}
												</div>
											)}
										</div>

										<Button
											onClick={() => setSelected(delivery)}
											className="flex-shrink-0 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 py-3"
										>
											<Play className="h-4 w-4 sm:mr-2" />
											<span className="hidden sm:inline">Start</span>
										</Button>
									</div>
								</div>
							))
						)}
					</div>

					{!loading && filtered.length > 0 && !searchQuery && (
						<div className="p-4 sm:p-6 border-t border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
							<p className="text-sm text-gray-500 dark:text-gray-400">
								Showing {displayedCount} of {totalCount} deliveries
							</p>
							{hasMore && (
								<Button variant="secondary" onClick={loadMore} disabled={loading}>
									{loading ? (
										<>
											<Loader2 className="h-4 w-4 mr-2 animate-spin" />
											Loading...
										</>
									) : (
										'Load More'
									)}
								</Button>
							)}
						</div>
					)}
				</div>
			</div>

			{selected && (
				<ShopReceiptModal
					delivery={selected}
					adminId={adminId}
					userId={user?.id ?? null}
					userName={user?.full_name ?? null}
					userEmail={user?.email ?? null}
					onClose={() => {
						setSelected(null)
						refresh()
					}}
					onComplete={handleComplete}
				/>
			)}
		</div>
	)
}

export default ShopTerminal
